import { useState } from 'react';
import { Box, CheckboxGroup } from '@chakra-ui/react';
import { Colors, Text, Checkbox } from '@mercator-gcp/grout';
import PopoverComponent from 'components/elements/Dropdown/PopoverComponent';
import { Spinner } from 'components/elements/Spinner';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import {
  formatPhaseLabelToString,
  getPhaseForDocumentType,
  getPhaseOrder,
} from 'utils/constants/projectPhases';
import { renameDocuments } from 'utils/renameDocuments';

// Import styles from ProjectStageDropdown
const phaseText = {
  px: 'padding_12',
  py: 'padding_8',
};

const optionContainer = {
  display: 'inline-flex',
  width: '100%',
  _hover: {
    background: 'brand.army-green-100',
  },
  _active: {
    background: 'brand.army-green-100',
  },
  pl: 'padding_12',
  pr: 'padding_12',
  pt: 'padding_4',
  pb: 'padding_4',
};

const checkboxStyle = {
  width: '100%',
};

const optionText = (hasChildren?: boolean) => ({
  letterSpacing: '-0.13px',
  fontSize: '13px',
  color: 'brand.army-dark',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: hasChildren ? '150px' : '180px',
  width: '100%',
});

interface PhaseGroupedOptionsProps {
  options: Array<{
    label: string;
    value: string | number;
    children?: Array<{
      label: string;
      value: string | number;
    }>;
  }>;
  checkedValues: (string | number)[];
  onChange: (values: string[]) => void;
  loading?: boolean;
  onChildrenChange?: (values: string[]) => void;
  childValues?: string[];
  variant?: 'negative' | 'positive';
}

export const PhaseGroupedOptions = ({
  options,
  checkedValues,
  onChange,
  loading,
  onChildrenChange,
  childValues = [],
  variant = 'positive',
}: PhaseGroupedOptionsProps) => {
  const [openPopovers, setOpenPopovers] = useState<Set<string>>(new Set());

  const optionsByPhase = options.reduce(
    (acc, option) => {
      const phase = getPhaseForDocumentType(option.label);
      if (!acc[phase]) acc[phase] = [];
      acc[phase].push(option);

      return acc;
    },
    {} as Record<string, typeof options>,
  );

  Object.values(optionsByPhase).forEach((phaseOptions) => {
    phaseOptions.sort((a, b) => {
      const phaseA = getPhaseForDocumentType(a.label);
      const phaseB = getPhaseForDocumentType(b.label);

      return getPhaseOrder(phaseA, a.label) - getPhaseOrder(phaseB, b.label);
    });
  });

  return (
    <FlexColumn>
      {Object.entries(optionsByPhase).map(([phase, phaseOptions]) => (
        <Box key={phase}>
          <Box sx={phaseText}>
            <Text.SmallLabel>{formatPhaseLabelToString(phase)}</Text.SmallLabel>
          </Box>
          <Box sx={{ height: '100%', overflowY: 'auto' }}>
            <CheckboxGroup onChange={onChange} value={checkedValues}>
              {phaseOptions.map((option) => {
                const hasChildren = option.children && option.children.length > 0;
                const optionKey = String(option.value);
                const isChildOpen = openPopovers.has(optionKey);

                const handlePopoverOpen = () => {
                  setOpenPopovers((prev) => new Set(prev).add(optionKey));
                };

                const handlePopoverClose = () => {
                  setOpenPopovers((prev) => {
                    const next = new Set(prev);
                    next.delete(optionKey);

                    return next;
                  });
                };

                return (
                  <FlexRow
                    sx={{
                      ...optionContainer,
                      // Maintain hover state when child filter is open
                      ...(isChildOpen && {
                        background: Colors.ArmyGreen100,
                      }),
                    }}
                    key={option.value}
                  >
                    <Checkbox value={option.value} sx={checkboxStyle} variant={variant}>
                      <Text sx={optionText(hasChildren)}>
                        {renameDocuments(option.label)}
                      </Text>
                    </Checkbox>
                    {hasChildren && (
                      <PopoverComponent
                        onOpen={handlePopoverOpen}
                        onClose={handlePopoverClose}
                      >
                        {loading ? (
                          <Spinner />
                        ) : (
                          <CheckboxGroup onChange={onChildrenChange} value={childValues}>
                            {option.children?.map((child) => (
                              <FlexRow sx={optionContainer} key={child.value}>
                                <Checkbox value={child.value} variant={variant}>
                                  <Text sx={optionText()}>
                                    {renameDocuments(child.label)}
                                  </Text>
                                </Checkbox>
                              </FlexRow>
                            ))}
                          </CheckboxGroup>
                        )}
                      </PopoverComponent>
                    )}
                  </FlexRow>
                );
              })}
            </CheckboxGroup>
          </Box>
        </Box>
      ))}
    </FlexColumn>
  );
};
